/**
 * سجلّ الإعدادات — كل رقم وسياسة في النظام، بمكان واحد.
 *
 * **لم يكن له وجود في v2**: حدّ المراجعات، ومدّة الاحتفاظ بالتقارير، وعتبات
 * التخزين، ومواعيد التذكير — كلّها ثوابت مدفونة في الكود. وكان ذلك أكبر فجوة
 * بين المنتج المبني والمنتج المُباع: كل وكالة تعمل بطريقتها، وأي اختلاف كان
 * يعني تعديلاً في الكود.
 *
 * لكل إعداد **شرحٌ بشري** — لأن من يقرؤه هو المشتري في شاشة الإعدادات، لا
 * مطوّر. والوصف هنا هو نفسه ما تعرضه الشاشة، فلا ينحرفان.
 */
export const settingsRegistry = {
    // ── النسخ الاحتياطي — **وجهةٌ بعيدة، ولا يكتمل المعالج بدونها** ──────────
    /**
     * §11: «وجهة بعيدة يحدّدها المشتري في معالج الإعداد… والنظام يتحقّق من عملها
     * قبل المتابعة. قرصٌ ثانٍ على السيرفر نفسه ليس نسخة احتياطية — يموت معه».
     *
     * والمفاتيح السرّية **لا تُخزَّن هنا**: تُتحقَّق عند الإعداد ثم تُحفظ في بيئة
     * التشغيل. فسجلُّ إعداداتٍ يُقرأ من شاشةٍ إدارية ليس موضع مفتاحٍ سرّي.
     */
    "backup.endpoint": {
        kind: "string",
        group: "retention",
        label: { ar: "وجهة النسخ الاحتياطي", en: "Backup destination" },
        help: {
            ar: "عنوان تخزين متوافق مع S3 خارج هذا الخادم. فأي قرص على الخادم نفسه يضيع معه إن تعطّل.",
            en: "An S3-compatible endpoint outside this server. A second disk on the same server dies with it.",
        },
        defaultValue: "",
        effect: {
            kind: "used",
            where: "apps/api setup → يُتحقَّق من عملها قبل اكتمال الإعداد؛ والجدولة مع موديول النسخ",
        },
    },
    "backup.bucket": {
        kind: "string",
        group: "retention",
        label: { ar: "دلو النسخ الاحتياطي", en: "Backup bucket" },
        help: {
            ar: "اسم الدلو الذي تُكتب فيه النسخ.",
            en: "The bucket backups are written into.",
        },
        defaultValue: "",
        effect: {
            kind: "used",
            where: "apps/api setup → يُتحقَّق من عملها قبل اكتمال الإعداد؛ والجدولة مع موديول النسخ",
        },
    },
    /**
     * **ومدّةُ حفظ السجلّ الجنائي — إعدادٌ لا رقمٌ في الكود** (قرار شادي ٢٠٢٦-١٠-١٩).
     *
     * فوكالةٌ تُلزمها جهةٌ بحفظ خمس سنين **تضبطها بنفسها ولا تنتظر نسخةً جديدة**.
     * **ولا يُنضَّف شيءٌ تلقائياً**: التنضيفُ فعلُ إنسانٍ بتحقُّقٍ ثانٍ وسببٍ
     * مكتوب — فجدولٌ يمحو نفسَه بالليل يمحو دليلاً كان سيُسأل عنه صباحاً.
     */
    "analytics.startsOn": {
        kind: "string",
        group: "retention",
        label: { ar: "بداية الأرقام والتحليلات", en: "Analytics start from" },
        help: {
            ar: "لا يدخل الأرقام ما قبل هذا اليوم. ويُضبط عند التشغيل الفعلي بصيغة 2026-12-01، والفارغ يعني كل التاريخ.",
            en: "Nothing before this day counts in the numbers. Set at go-live as 2026-12-01; empty means all history.",
        },
        defaultValue: "",
        effect: {
            kind: "used",
            where: "apps/api analytics → يقصّ كلَّ فترةٍ من أوّلها، في موضعٍ واحد",
        },
    },
    "platform.auditRetentionMonths": {
        kind: "number",
        group: "retention",
        label: { ar: "حفظ سجلّ النشاط (شهور)", en: "Keep activity log (months)" },
        help: {
            ar: "أقل مدة يُحفظ فيها سجلّ «من فعل ماذا». والتنظيف يتمّ بزرّ من الإدارة، لا تلقائياً.",
            en: "The minimum period the who-did-what log is kept. Purging is a deliberate admin action, never automatic.",
        },
        defaultValue: 24,
        min: 6,
        max: 120,
        effect: {
            kind: "used",
            where: "apps/api platform → حدُّ ما يجوز تنظيفه، ويُقرأ في المسار قبل المسح",
        },
    },
    // ── الهوية — ما يغيّره المشتري في معالج الإعداد ──────────────────────────
    "identity.systemName": {
        kind: "string",
        group: "identity",
        label: { ar: "اسم النظام", en: "System name" },
        help: {
            ar: "يظهر في الشريط العلوي وفي عنوان المتصفح وفي الإشعارات.",
            en: "Shown in the top bar, the browser title, and notifications.",
        },
        /** **واسمُ المنتج مِنوال** — وكان `Planner` (وثيقةُ الهوية، ٢٠٢٦-١٠-٢٠) */
        defaultValue: "مِنوال",
        effect: { kind: "used", where: "apps/api bootstrap → شاشة الدخول والقوقعة" },
    },
    /**
     * **وعلامةُ مِنوال في القوقعة — لتنصيب TWING وحده.**
     *
     * فالمنتجُ يُباع بلا علامةٍ بائعة: **أوّلُ حرفٍ من اسم المشتري بلونه** هو علامتُه.
     * أمّا تنصيبُ TWING نفسُه فيحمل علامةَ مِنوال (وثيقةُ الهوية). **والافتراضُ
     * مطفأ** — فمشترٍ لم يغيّره لا يرى علامةَ غيره في نظامه.
     */
    "identity.minwalMark": {
        kind: "boolean",
        group: "identity",
        label: { ar: "علامة مِنوال", en: "Minwal mark" },
        help: {
            ar: "اعرض شعار مِنوال بدل أول حرف من اسم النظام. هذا لتنصيب TWING — والمشتري يحتفظ بعلامته هو.",
            en: "Show the Minwal logo instead of the system name's first letter. For TWING's install; buyers keep their own mark.",
        },
        defaultValue: false,
        effect: { kind: "used", where: "apps/api /identity → علامةُ القوقعة" },
    },
    "identity.accentColor": {
        kind: "color",
        group: "identity",
        label: { ar: "لون النظام", en: "System colour" },
        help: {
            ar: "لون واحد للأزرار الأساسية والعنصر المختار. الأخضر والأصفر والأحمر محجوزة للحالات، لكي تبقى «معتمَد» و«متأخّر» واضحة.",
            en: "One colour for primary buttons and the active item. Green, amber and red stay reserved for status.",
        },
        defaultValue: "#0B6E75",
        effect: { kind: "used", where: "apps/api bootstrap → توكنز الهوية على الجذر" },
    },
    "identity.defaultLocale": {
        kind: "string",
        group: "identity",
        label: { ar: "اللغة الافتراضية", en: "Default language" },
        help: {
            ar: "اللغة التي يراها أي مستخدم جديد عند أول دخول.",
            en: "The language a new user sees on first sign-in.",
        },
        defaultValue: "ar",
        shape: "locale",
        effect: { kind: "used", where: "apps/api bootstrap → لغة الواجهة قبل الدخول" },
    },
    "identity.systemTimezone": {
        kind: "string",
        group: "identity",
        label: { ar: "توقيت النظام", en: "System timezone" },
        help: {
            ar: "تُعرض بها شاشات الموظفين. وشاشات العميل بتوقيته هو.",
            en: "Staff screens use this. Client screens use the client's own timezone.",
        },
        defaultValue: "Africa/Cairo",
        /** **ومنطقةٌ تُختار باسم مدينتها** — لا `Africa/Cairo` يُكتَب بيدٍ تُخطئ حرفاً */
        shape: "timeZone",
        effect: { kind: "used", where: "apps/api bootstrap → تنسيق كل وقتٍ في الشاشات" },
    },
    // ── الخطط ────────────────────────────────────────────────────────────────
    "plans.revisionLimit": {
        kind: "number",
        group: "plans",
        label: { ar: "حدّ مراجعات العميل", en: "Client revision limit" },
        help: {
            ar: "كم مرة يرفض العميل العنصر قبل أن يتوقف ويحتاج استثناءً من الإدارة. والاستثناء يعيد العدّاد إلى ما كان، ولا يصفّره.",
            en: "How many times a client may reject an item before it parks and needs an administrative exception. Granting restores the prior count; it does not reset it.",
        },
        defaultValue: 3,
        min: 1,
        max: 10,
        effect: { kind: "used", where: "apps/api/src/plans/routes.ts" },
    },
    "plans.revisionExceptionEscalationHours": {
        kind: "number",
        group: "plans",
        label: { ar: "تصعيد الاستثناء غير المحسوم", en: "Unresolved exception escalation" },
        help: {
            ar: "بعد كم ساعة يُصعَّد إلى الإدارة استثناء المراجعة الذي لم يردّ عليه أحد.",
            en: "After how many hours an unresolved revision exception escalates.",
        },
        defaultValue: 24,
        min: 1,
        max: 168,
        effect: { kind: "used", where: "apps/api/src/plans/routes.ts" },
    },
    // ── الشغل ────────────────────────────────────────────────────────────────
    "work.dueReminderHours": {
        kind: "string",
        group: "work",
        label: { ar: "التذكير قبل الموعد", en: "Reminders before the due time" },
        help: {
            ar: "قبل الموعد بكم ساعة يصل التذكير — ويمكنك إضافة أكثر من تذكير.",
            en: "Comma-separated hours before the due time. Example: 24,12,3",
        },
        defaultValue: "24,12,3",
        shape: "hourList",
        effect: { kind: "used", where: "apps/api/src/cards/reminders.ts" },
    },
    "work.overdueDailyReminder": {
        kind: "boolean",
        group: "work",
        label: { ar: "تذكير يومي بعد فوات الموعد", en: "Daily reminder once overdue" },
        help: {
            ar: "«فات موعده منذ كذا يوماً» — كل يوم حتى يُنجَز العمل.",
            en: "“Overdue by n days” — every day until the work is finished.",
        },
        defaultValue: true,
        effect: { kind: "used", where: "apps/api/src/cards/reminders.ts" },
    },
    // ── التقارير ─────────────────────────────────────────────────────────────
    "reports.availabilityDays": {
        kind: "number",
        group: "reports",
        label: { ar: "مدّة إتاحة التقرير للعميل", en: "Report availability for the client" },
        help: {
            ar: "بعد كم يوماً يختفي التقرير المنشور من بوابة العميل. ولا يرى العميل عدّاداً تنازلياً.",
            en: "After how many days a published report leaves the client portal. No countdown is shown to the client.",
        },
        defaultValue: 365,
        min: 30,
        max: 3650,
        effect: { kind: "pending", because: "ينزل مع موديول التقارير في المرحلة التاسعة" },
    },
    // ── التخزين ──────────────────────────────────────────────────────────────
    "storage.warnPercent": {
        kind: "number",
        group: "storage",
        label: { ar: "تنبيه التخزين", en: "Storage warning" },
        help: {
            ar: "عند أي نسبة من المساحة يصل أول تنبيه.",
            en: "At what fill percentage the first warning is sent.",
        },
        defaultValue: 70,
        min: 10,
        max: 99,
        effect: { kind: "used", where: "apps/api/src/clients/quota.ts" },
    },
    /**
     * العتبة الوسطى — **معلَنةٌ كأختيها، لا محسوبةٌ بينهما**.
     *
     * لو اشتُقّت من الاثنتين لصار للعتبة الثالثة قاعدةٌ في الكود لا يعرفها المشتري،
     * وهو بعينه ما شكا منه تدقيق v2: «أرقامٌ مدفونة في الكود».
     */
    "storage.criticalPercent": {
        kind: "number",
        group: "storage",
        label: { ar: "تنبيه حرج للتخزين", en: "Critical storage warning" },
        help: {
            ar: "عند أي نسبة يصبح التنبيه حرجاً — بين أول تنبيه وإيقاف الرفع.",
            en: "At what percentage the warning becomes critical — between the first warning and the upload stop.",
        },
        defaultValue: 85,
        min: 20,
        max: 99,
        effect: { kind: "used", where: "apps/api/src/clients/quota.ts" },
    },
    /**
     * الحدّ الافتراضي لأي عميل جديد — **قرار شادي ٢٠٢٦-٠٩-٢٦: عشرون جيجابايت**.
     *
     * وهو موضع «قيمتُه الافتراضية إعدادٌ من إعدادات النظام» في §4.3. فمن ينشئ
     * عميلاً يقرأ هذا، ولا يوجد افتراضٌ في القاعدة يتسلّل منه رقمٌ صامت.
     */
    "storage.defaultClientQuotaGb": {
        kind: "number",
        group: "storage",
        label: { ar: "مساحة العميل الجديد", en: "New client's storage" },
        help: {
            ar: "المساحة التي يبدأ بها أي عميل جديد بالغيغابايت. ويمكنك زيادتها لأي عميل على حدة من شاشته.",
            en: "How many gigabytes a new client starts with. It is raised per client from its own screen.",
        },
        defaultValue: 20,
        min: 1,
        max: 5000,
        effect: { kind: "used", where: "apps/api/src/clients/service.ts" },
    },
    "storage.blockPercent": {
        kind: "number",
        group: "storage",
        label: { ar: "إيقاف الرفع", en: "Upload stop" },
        help: {
            ar: "عند أي نسبة يتوقف الرفع. ويبقى الفتح والحذف متاحَين دائماً.",
            en: "At what percentage uploads stop. Reading and deleting always stay available.",
        },
        defaultValue: 95,
        min: 50,
        max: 100,
        effect: { kind: "used", where: "apps/api/src/clients/quota.ts" },
    },
    // ── الأمان ───────────────────────────────────────────────────────────────
    "security.stepUpWindowHours": {
        kind: "number",
        group: "security",
        label: { ar: "صلاحية التحقّق الثاني", en: "Step-up verification window" },
        help: {
            ar: "بعد كم ساعة يُطلب التحقّق الثاني مجدداً قبل العمليات الكبيرة.",
            en: "After how many hours a major operation asks for a second verification again.",
        },
        defaultValue: 4,
        min: 1,
        max: 24,
        effect: { kind: "used", where: "apps/api guard → مهلة صلاحية التحقّق الثاني" },
    },
    "security.delegationReviewDays": {
        kind: "number",
        group: "security",
        label: { ar: "تذكير مراجعة التفويض", en: "Delegation review notice" },
        help: {
            ar: "كل كم يوماً يُنبَّه المالك ومدير النظام إلى وجود تفويض ما زال سارياً. هذا إشعار فقط — ولا يوقف التفويض.",
            en: "How often the owner and system manager are told a delegation is still running. A notice only — it never stops or expires the delegation.",
        },
        defaultValue: 90,
        min: 7,
        max: 365,
        effect: { kind: "pending", because: "ينزل مع تذكير مراجعة التفويضات في المرحلة الحادية عشرة" },
    },
    // ── الاحتفاظ ─────────────────────────────────────────────────────────────
    "retention.archiveWorkAfterYears": {
        kind: "number",
        group: "retention",
        label: { ar: "أرشفة العمل المنتهي", en: "Archive finished work" },
        help: {
            ar: "بعد كم سنة يخرج العمل المنتهي من الشاشات اليومية. هذا أرشيف لا حذف — يبقى موجوداً ويمكن استعادته.",
            en: "After how many years finished work leaves the daily screens. Archiving, not deletion — it stays readable and retrievable.",
        },
        defaultValue: 2,
        min: 1,
        max: 10,
        effect: { kind: "pending", because: "ينزل مع سياسات الاحتفاظ في المرحلة الحادية عشرة" },
    },
    "retention.auditMinimumAgeDays": {
        kind: "number",
        group: "retention",
        label: { ar: "أقلّ عمر لحذف سجلّ جنائي", en: "Minimum age before audit deletion" },
        help: {
            ar: "أي حدث أحدث من هذا العمر لا يُحذف أبداً، ولا يُحذف شيء دون قرار صريح.",
            en: "No event younger than this may be deleted under any circumstance, and nothing is deleted without an explicit decision.",
        },
        defaultValue: 180,
        min: 90,
        max: 3650,
        effect: { kind: "pending", because: "ينزل مع سياسات الاحتفاظ في المرحلة الحادية عشرة" },
    },
    "retention.chatExpiryDays": {
        kind: "number",
        group: "retention",
        label: { ar: "انتهاء المحادثات الخاصة", en: "Private conversation expiry" },
        help: {
            ar: "تنتهي المحادثة الخاصة بعد كم يوماً من آخر رسالة فيها. أما العامّة ومحادثات العملاء فلا تنتهي.",
            en: "How many days after the last real message a private conversation expires. General and client channels never expire.",
        },
        defaultValue: 30,
        min: 7,
        max: 365,
        effect: { kind: "pending", because: "ينزل مع المحادثات في المرحلة الثامنة" },
    },
    // ── إشعارُ الدفع — **والسرُّ ليس هنا** ──────────────────────────────────
    /**
     * §4.5: «إشعاراتُ ويب قياسية، بلا Firebase. ومفاتيحُها في الإعدادات، **والسرُّ
     * في بيئة التشغيل لا في جدولٍ يُقرأ من شاشة**».
     *
     * فالمفتاحُ العامّ يخرج إلى المتصفّح أصلاً — لا سرَّ فيه، وموضعُه الإعدادات
     * فيغيّره المشتري بلا نشرٍ جديد. والسرُّ في `PUSH_PRIVATE_KEY` من بيئة
     * التشغيل، **وغيابُه يُقال بكوده** `PUSH_NOT_CONFIGURED` لا يُسكَت عليه.
     */
    "portal.pushPublicKey": {
        kind: "string",
        group: "security",
        label: { ar: "مفتاح تنبيهات الأجهزة العامّ", en: "Device notification public key" },
        help: {
            ar: "المفتاح العامّ الذي يثبّت به المتصفّح اشتراكه في التنبيهات. أما سرّه ففي بيئة التشغيل، لا هنا.",
            en: "The public key the browser pins its subscription to. Its matching secret lives in the runtime environment, not here.",
        },
        defaultValue: "",
        effect: {
            kind: "used",
            where: "apps/api/src/portal/push.ts → يُقرأ عند كل إرسال، ويُعرَض للمتصفّح في إعداد التشغيل",
        },
    },
    "portal.pushContact": {
        kind: "string",
        group: "security",
        label: { ar: "بريد التواصل لخدمة التنبيهات", en: "Push service contact address" },
        help: {
            ar: "البريد الذي تتواصل عبره معك خدمة التنبيهات إن حدثت مشكلة — والمعيار نفسه يطلبه.",
            en: "An address the push service contacts about problems — the standard itself requires it.",
        },
        defaultValue: "",
        effect: {
            kind: "used",
            where: "apps/api/src/portal/push.ts → موضوع الـVAPID في كل إرسال",
        },
    },
};
export const SETTING_KEYS = Object.keys(settingsRegistry);
export function settingDefinition(key) {
    return settingsRegistry[key];
}
/** القيم الافتراضية — تكتبها البذرة، ويقرؤها النظام حتى قبل أن يلمسها أحد. */
export function defaultSettings() {
    const out = {};
    for (const key of SETTING_KEYS)
        out[key] = settingsRegistry[key].defaultValue;
    return out;
}
/** يرفض قيمة خارج حدود الإعداد — الرقم الخاطئ يُمنَع عند الكتابة لا بعدها. */
export function validateSetting(key, value) {
    const def = settingDefinition(key);
    if (def.kind === "number") {
        if (typeof value !== "number" || !Number.isFinite(value))
            return "expected a number";
        if (def.min !== undefined && value < def.min)
            return `must be at least ${def.min}`;
        if (def.max !== undefined && value > def.max)
            return `must be at most ${def.max}`;
        return null;
    }
    if (def.kind === "boolean")
        return typeof value === "boolean" ? null : "expected true or false";
    if (def.kind === "color") {
        return typeof value === "string" && /^#[0-9a-fA-F]{6}$/.test(value)
            ? null
            : "expected a colour like #0B6E75";
    }
    return typeof value === "string" && value.trim() !== "" ? null : "expected a non-empty string";
}
//# sourceMappingURL=registry.js.map